import { translateTemplate } from 'common-translations/src/vtj/translate-utils'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import type { TFunction } from 'i18next'
import CertificateTypes, {
  VtjOteTodistusType,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { maritalStatusBlocksAet } from 'todistukset-common/src/vtj/MaritalStatus.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  isEidasIdentifiedUser,
  isHetuIdentifiedUser,
} from 'todistukset-common/src/vtj/user.utils'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import InfoPage from 'todistukset-ui/src/vtj/components/info-page/InfoPage.react'
import LandingPage from 'todistukset-ui/src/vtj/components/landing-page/LandingPage.react'
import MyCertificates from 'todistukset-ui/src/vtj/components/my-certificates-page/MyCertificates.react'
import { getAetOrdererSteps } from 'todistukset-ui/src/vtj/components/order-wizard-aet-orderer/aetOrdererSteps'
import { getAetSignerSteps } from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/aetSignerSteps'
import OrderWizardPage from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardPage.react'
import { type Step } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { getVtjOteTodistusSteps } from 'todistukset-ui/src/vtj/components/order-wizard/todistusSteps'
import { PageConfig } from 'todistukset-ui/src/vtj/components/page-router/TodistuksetRouter.types'
import i18next from 'todistukset-ui/src/vtj/i18next'
import PageRoutes from 'todistukset-ui/src/vtj/todistukset-page-routes.enum'
import { getStepTitleKey } from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import { getUaetSteps } from 'todistukset-ui/src/vtj/components/order-wizard-uaet/uaetSteps'

const pages: Record<string, PageConfig> = {
  [PageRoutes.LANDING]: {
    component: LandingPage,
    tabIndex: 0,
    getTitle: getTranslator('{orderingTodistukset} - {mainTitle}'),
    requiresAuthentication: false,
  },
  [PageRoutes.INFO]: {
    component: InfoPage,
    getTitle: getTranslator('{moreInfo} - {mainTitle}'),
    tabIndex: 1,
    requiresAuthentication: false,
  },
  [PageRoutes.MY_CERTIFICATES]: {
    component: MyCertificates,
    getTitle: getTranslator(
      '{navigationTabsOrderedCertificatesTitle} - {mainTitle}'
    ),
    tabIndex: 2,
    requiresAuthentication: true,
  },
  [PageRoutes.ELOSSAOLOTODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getVtjOteSteps(CertificateTypes.ELOSSAOLOTODISTUS),
  },
  [PageRoutes.ASUINPAIKKATODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getVtjOteSteps(CertificateTypes.ASUINPAIKKATODISTUS),
  },
  [PageRoutes.AVIOLIITTOTODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getVtjOteSteps(CertificateTypes.AVIOLIITTOTODISTUS),
  },
  [PageRoutes.SIVIILISAATYTODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getVtjOteSteps(CertificateTypes.SIVIILISAATYTODISTUS),
  },
  [PageRoutes.SYNTYMATODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getVtjOteSteps(CertificateTypes.SYNTYMATODISTUS),
  },
  [PageRoutes.AETTODISTUS_ORDERER]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getAetOrdererSteps,
  },
  [PageRoutes.AETTODISTUS_SIGNER]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getAetSignerStepsF(),
  },
  [PageRoutes.UAETTODISTUS]: {
    component: OrderWizardPage,
    getTitle: getStepTitle,
    tabIndex: 0,
    requiresAuthentication: true,
    isStep: true,
    getSteps: getUaetSteps,
  },
}

export default pages

export function getVtjOteSteps(
  certificateType: VtjOteTodistusType
): ({ store }: { store: TodistuksetUi.Store }) => Step[] {
  return ({ store }: { store: TodistuksetUi.Store }) => {
    const { loggedInUser } = store.common
    const user = loggedInUser.get()
    const isEidasUser = isEidasIdentifiedUser(user)
    const steps = getVtjOteTodistusSteps(certificateType, isEidasUser)
    return steps
  }
}

export function getAetSignerStepsF(): ({
  store,
  pathname,
}: {
  store: TodistuksetUi.Store
  pathname: string
}) => Step[] {
  return ({
    store,
    pathname,
  }: {
    store: TodistuksetUi.Store
    pathname: string
  }) => {
    const { loggedInUser } = store.common
    const user = loggedInUser.get()
    const userCanSubmitAet =
      isHetuIdentifiedUser(user) &&
      !maritalStatusBlocksAet(
        finalEditableFieldValue(user.maritalStatus) ?? MaritalStatus.EI_TIETOA
      )
    return getAetSignerSteps({
      pathname,
      onlyDeclineIsAllowed: !userCanSubmitAet,
    })
  }
}

function getTranslator(templateText: string) {
  return (lang: LanguageCode) =>
    translateTemplate({ translations: i18next, lang, templateText })
}

function getStepTitle(
  translate: TFunction,
  page: string,
  step: string
): string {
  const key = getStepTitleKey(page, step)
  return key
    ? `${translate(key)} - ${translate('mainTitle')}`
    : translate('mainTitle')
}
